import { useCallback, useRef, useState } from 'react';

const isTouchEvent = (event) => 'touches' in event;

const preventDefault = (event) => {
    if (!isTouchEvent(event)) return;

    if (event.touches.length < 2 && event.preventDefault) {
        event.preventDefault();
    }
};

const useLongPress = (
    onLongPress,
    onClick,
    { delay = 300, shouldPreventDefault = true } = {},
) => {
    const [longPressTriggered, setLongPressTriggered] = useState(false);
    const timeout = useRef();
    const target = useRef();

    const start = useCallback(
        (event) => {
            if (shouldPreventDefault && event.target) {
                event.target.addEventListener('touchend', preventDefault, {
                    passive: false,
                });
                target.current = event.target;
            }
            timeout.current = setTimeout(() => {
                onLongPress(event);
                setLongPressTriggered(true);
            }, delay);
        },
        [onLongPress, delay, shouldPreventDefault],
    );

    const clear = useCallback(
        (event, shouldTriggerClick = true) => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }

            if (shouldTriggerClick && !longPressTriggered) {
                onClick();
            }

            setLongPressTriggered(false);
            if (shouldPreventDefault && target.current) {
                target.current.removeEventListener('touchend', preventDefault);
            }
        },
        [shouldPreventDefault, onClick, longPressTriggered],
    );

    return {
        onMouseDown: (e) => start(e),
        onMouseLeave: (e) => clear(e, false),
        onMouseUp: (e) => clear(e),
        onTouchEnd: (e) => clear(e),
        onTouchStart: (e) => start(e),
    };
};

export default useLongPress;
